import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const KontaktPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/api/contact`, formData);
      setSent(true);
      toast.success('Ihre Nachricht wurde erfolgreich gesendet!');
    } catch (error) {
      toast.error('Fehler beim Senden: ' + (error.response?.data?.detail || 'Bitte versuchen Sie es später erneut'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div className="text-center p-8">
          <CheckCircle2 className="w-20 h-20 text-green-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Vielen Dank!</h1>
          <p className="text-gray-400 mb-8">
            Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns so schnell wie möglich bei Ihnen melden.
          </p>
          <Link to="/">
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white">
              Zurück zur Startseite
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F14', color: '#E6EDF3' }}>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-8"
        >
          <ArrowLeft size={20} />
          Zurück zur Startseite
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#1EA0E6' }}>
              Kontakt
            </h1>
            <p className="text-gray-400 mb-8">
              Haben Sie Fragen, Anregungen oder benötigen Sie Unterstützung? 
              Wir sind für Sie da und freuen uns auf Ihre Nachricht.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">E-Mail</h3>
                  <a href="mailto:support@carcortex-ai.com" className="text-cyan-400 hover:underline">
                    support@carcortex-ai.com
                  </a>
                  <p className="text-sm text-gray-500 mt-1">
                    Antwort innerhalb von 24-48 Stunden
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Telefon</h3>
                  <p className="text-gray-300">Auf Anfrage</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Für dringende Anfragen
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Adresse</h3>
                  <p className="text-gray-300">
                    Andreas Trenter<br />
                    STR-Performance<br />
                    Dorfweg 8<br />
                    4718 Holderbank, Schweiz
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl border border-cyan-500/30">
              <h3 className="font-semibold text-white mb-2">Support-Zeiten</h3>
              <p className="text-gray-400 text-sm">
                Montag - Freitag: 09:00 - 18:00 Uhr (MEZ)<br />
                Wochenende: Nur per E-Mail
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#111827] rounded-2xl p-8 border border-gray-800">
            <h2 className="text-2xl font-bold text-white mb-6">Nachricht senden</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  placeholder="Ihr Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  E-Mail *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  placeholder="ihre@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Betreff
                </label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                >
                  <option value="">Bitte wählen...</option>
                  <option value="support">Technischer Support</option>
                  <option value="billing">Abrechnung & Zahlung</option>
                  <option value="feedback">Feedback & Vorschläge</option>
                  <option value="partnership">Partnerschaft & Kooperation</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nachricht *
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors resize-none"
                  placeholder="Wie können wir Ihnen helfen?"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-3 text-lg"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Nachricht senden
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Mit dem Absenden erklären Sie sich mit unserer{' '}
                <Link to="/datenschutz" className="text-cyan-400 hover:underline">
                  Datenschutzerklärung
                </Link>{' '}
                einverstanden.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KontaktPage;
